import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {Subject, Subscription} from 'rxjs';
import {debounceTime, distinctUntilChanged, filter, switchMap} from 'rxjs/operators';
import {HelpArticleService} from "../../service/api/help-article.service";
import {HelpSearchResultDto} from "../../model/dto/help-article.dto";

/**
 * Search component for help articles.
 * Provides debounced full-text search against CbHelpSearchService (PostgreSQL FTS).
 */
@Component({
    selector: 'mvs-help-search',
    templateUrl: './help-search.component.html',
    styleUrls: ['./help-search.component.scss'],
    standalone: false
})
export class HelpSearchComponent implements OnInit, OnChanges, OnDestroy {

    @Input() moduleAlias: string;
    @Input() placeholder = 'Dokumentation durchsuchen...';
    @Output() articleSelected = new EventEmitter<HelpSearchResultDto>();

    initialized = false;
    searchQuery = '';
    results: HelpSearchResultDto[] = [];
    isSearching = false;
    showResults = false;

    private searchSubject = new Subject<string>();
    private subscriptions: Subscription[] = [];

    constructor(private articleService: HelpArticleService) {}

    ngOnInit(): void {
        this.initComponent();
    }

    /**
     * Initialize Component.
     */
    initComponent(): void {
        const sub = this.searchSubject.pipe(
            debounceTime(300),
            distinctUntilChanged(),
            filter(query => query.length >= 2),
            switchMap(query => {
                this.isSearching = true;
                return this.articleService.searchArticles(query, this.moduleAlias);
            })
        ).subscribe({
            next: results => {
                this.results = results;
                this.isSearching = false;
                this.showResults = true;
            },
            error: err => {
                console.error('Search error:', err);
                this.isSearching = false;
            }
        });
        this.subscriptions.push(sub);
        this.initialized = true;
    }

    /**
     * Process changes within Binding.
     * @param changes
     */
    ngOnChanges(changes: SimpleChanges): void {
        if (!this.initialized) {
            return;
        }
    }

    /**
     * Destroy component.
     */
    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }

    onSearchInput(): void {
        if (this.searchQuery.length < 2) {
            this.results = [];
            this.showResults = false;
            return;
        }
        this.searchSubject.next(this.searchQuery);
    }

    onResultClick(result: HelpSearchResultDto): void {
        this.articleSelected.emit(result);
        this.showResults = false;
        this.searchQuery = '';
    }

    onBlur(): void {
        // Delay hiding results to allow click event
        setTimeout(() => this.showResults = false, 200);
    }

    onFocus(): void {
        if (this.results.length > 0) {
            this.showResults = true;
        }
    }

    clearSearch(): void {
        this.searchQuery = '';
        this.results = [];
        this.showResults = false;
    }
}
